import {
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { IconCheckmark2 } from 'central-icons/IconCheckmark2';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';
import { useMutation } from 'urql';

import { DELETE_SAVED_SET, UPDATE_SAVED_SET } from '@revelio/data-access';
import { NavigationButton } from '@revelio/ui';

import type { Option } from '../../filters/types/filters';
import type { UserSavedFilterSet } from './useGetStoredSets';

export type SavedSetOptionLike = Option<UserSavedFilterSet> & {
  entity: UserSavedFilterSet;
};

interface SetsListProps {
  options: SavedSetOptionLike[];
  onApply: (opt: SavedSetOptionLike) => void;
  onUpdated?: (opt: UserSavedFilterSet) => void;
  onDeleted?: (opt: UserSavedFilterSet) => void;
  emptySlot?: React.ReactNode;
  deleteSlot?: (opt: SavedSetOptionLike) => React.ReactNode;
  showSearch?: boolean;
}

export const SetsList = ({
  options,
  onApply,
  onUpdated,
  onDeleted,
  emptySlot,
  deleteSlot,
  showSearch = false,
}: SetsListProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toDeleteId, setToDeleteId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showSearch) {
      // autofocus when enabling search
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    } else {
      setSearch('');
    }
  }, [showSearch]);

  const toast = useToast();

  const [, updateSavedSet] = useMutation(UPDATE_SAVED_SET);
  const [, deleteSavedSet] = useMutation(DELETE_SAVED_SET);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  // When there are no options at all (before any search), defer to provided emptySlot logic
  if (options.length === 0) {
    return (
      emptySlot ?? (
        <Text fontSize="sm" color="gray.500">
          No saved sets found
        </Text>
      )
    );
  }

  const deleteOpt: SavedSetOptionLike | undefined = filtered.find(
    (o) => o.value === toDeleteId
  );

  const handleSave = (opt: SavedSetOptionLike, name: string) => {
    const safeName = DOMPurify.sanitize(name, { ALLOWED_TAGS: [] });

    // no change, do nothing
    if (opt.entity.name === name) {
      setEditingId(null);
      return;
    }

    // empty name, do nothing
    if (safeName.length === 0) {
      setEditingId(null);
      return;
    }

    setSavingId(opt.value);
    updateSavedSet({ id: opt.value, name: safeName })
      .then((res) => {
        if (res.error) {
          toast({
            title: 'Could not rename saved set',
            status: 'error',
            position: 'top-right',
            isClosable: true,
          });
        } else {
          toast({
            title: 'Saved set renamed',
            status: 'success',
            position: 'top-right',
            isClosable: true,
          });
        }
      })
      .finally(() => {
        setEditingId(null);
        setSavingId(null);
        onUpdated?.(opt.entity);
      });
  };

  const handleDelete = (opt: SavedSetOptionLike) => {
    setDeletingId(opt.value);
    deleteSavedSet({ id: opt.value })
      .then((res) => {
        if (res.error) {
          toast({
            title: 'Could not delete saved set',
            status: 'error',
            position: 'top-right',
            isClosable: true,
          });
        } else {
          toast({
            title: 'Saved set deleted',
            status: 'success',
            position: 'top-right',
            isClosable: true,
          });
        }
      })
      .finally(() => {
        onDeleted?.(opt.entity);
        setDeletingId(null);
        setToDeleteId(null);
      });
  };

  const deleteDialog = (opt: SavedSetOptionLike) => (
    <>
      {deleteSlot ? (
        deleteSlot(opt)
      ) : (
        <Text fontSize="sm">
          Are you sure you want to delete <b>{opt.label}</b> saved set?
        </Text>
      )}
      <Flex gap={2} justify="flex-end">
        <Button
          size="sm"
          variant="ghost"
          colorScheme="navyBlue"
          onClick={() => setToDeleteId(null)}
          isDisabled={deletingId === opt.value}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          colorScheme="red"
          isLoading={deletingId === toDeleteId}
          onClick={() => handleDelete(opt)}
        >
          Delete
        </Button>
      </Flex>
    </>
  );

  const editDialog = (opt: SavedSetOptionLike) => (
    <Flex key={opt.value} align="center" gap={2}>
      <Input
        data-testid={`saved-set-item-name-input-${opt.value}`}
        ref={inputRef}
        size="sm"
        value={editingName}
        variant="flushed"
        onChange={(e) => setEditingName(e.target.value)}
        flex={1}
        px={2.5}
        maxLength={255}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSave(opt, editingName);
          } else if (e.key === 'Escape') {
            setEditingId(null);
          }
        }}
      />
      <IconButton
        data-testid={`saved-set-item-save-${opt.value}`}
        isDisabled={editingName.trim().length === 0}
        size="xs"
        variant="ghost"
        colorScheme="navyBlue"
        aria-label="Save"
        borderRadius="4px"
        icon={<IconCheckmark2 size={14} />}
        isLoading={savingId === opt.value}
        onClick={() => handleSave(opt, editingName)}
      />
    </Flex>
  );

  const highlight = (label: string) => {
    if (!search) return label;
    const idx = label.toLowerCase().indexOf(search.toLowerCase());
    if (idx === -1) return label;
    const before = label.slice(0, idx);
    const match = label.slice(idx, idx + search.length);
    const after = label.slice(idx + search.length);
    return (
      <>
        {before}
        <mark>{match}</mark>
        {after}
      </>
    );
  };

  const item = (opt: SavedSetOptionLike) => (
    <NavigationButton
      data-testid={`saved-set-item-${opt.value}`}
      key={opt.value}
      isSelected={false}
      size="sm"
      onClick={() => onApply(opt)}
      alwaysShowSlot={menuOpenId === opt.value}
      slot={
        <Menu
          placement="bottom-end"
          offset={[0, 2]}
          isOpen={menuOpenId === opt.value}
          onOpen={() => setMenuOpenId(opt.value)}
          onClose={() => setMenuOpenId(null)}
        >
          <MenuButton
            data-testid={`saved-set-item-actions-${opt.value}`}
            as={IconButton}
            icon={<IconDotGrid1x3Vertical size={14} />}
            aria-label="Actions"
            size="xs"
            variant="ghost"
            colorScheme="navyBlue"
            borderRadius="4px"
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
          />
          <MenuList minW={24}>
            <MenuItem
              data-testid={`saved-set-item-rename-${opt.value}`}
              onClick={(e) => {
                e.stopPropagation();
                setEditingId(opt.value);
                setEditingName(opt.label);
                setMenuOpenId(null);
                setTimeout(() => {
                  inputRef.current?.focus();
                }, 0);
              }}
            >
              Rename
            </MenuItem>
            <MenuItem
              data-testid={`saved-set-item-delete-${opt.value}`}
              onClick={(e) => {
                e.stopPropagation();
                setToDeleteId(opt.value);
              }}
            >
              Delete
            </MenuItem>
          </MenuList>
        </Menu>
      }
    >
      {highlight(opt.label)}
    </NavigationButton>
  );

  return (
    <Stack spacing={2} flexGrow={1}>
      {showSearch && (
        <InputGroup size="sm" mb={1}>
          <Input
            ref={searchInputRef}
            placeholder="Search saved sets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <InputRightElement>
              <IconButton
                bg="transparent !important"
                aria-label="Clear search"
                size="xs"
                variant="ghost"
                colorScheme="navyBlue"
                icon={<IconCrossLarge size={12} />}
                onClick={() => {
                  setSearch('');
                  searchInputRef.current?.focus();
                }}
              />
            </InputRightElement>
          )}
        </InputGroup>
      )}
      {deleteOpt
        ? deleteDialog(deleteOpt)
        : filtered.map((opt) =>
            editingId === opt.value ? editDialog(opt) : item(opt)
          )}

      {!deleteOpt && filtered.length === 0 && (
        <Text fontSize="sm" color="gray.500" textAlign="center" p={4}>
          No filter sets found
        </Text>
      )}
    </Stack>
  );
};
